import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";

const platformIcons = {
  LinkedIn: (
    <svg width="16" height="16" viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="4" fill="#0077B5"/>
      <path d="M16.5 19.5H11.5V36.5H16.5V19.5Z" fill="white"/>
      <path d="M14 11.5C12.067 11.5 10.5 13.067 10.5 15C10.5 16.933 12.067 18.5 14 18.5C15.933 18.5 17.5 16.933 17.5 15C17.5 13.067 15.933 11.5 14 11.5Z" fill="white"/>
      <path d="M37.5 25C37.5 21.41 34.09 19 30.5 19C28.5 19 26.5 20 25.5 21.5V19.5H20.5V36.5H25.5V25.5C25.5 23.567 26.567 22.5 28.5 22.5C30.433 22.5 31.5 23.567 31.5 25.5V36.5H36.5V25H37.5Z" fill="white"/>
    </svg>
  ),
  CVLibrary: (
    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">CV</span>
  ),
};

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const [jobResponse, linksResponse] = await Promise.all([
          axios.get(`${jobsEndpoint}${id}/details/`, {
            headers: { Authorization: `Token ${token}` },
          }),
          axios.get(`${jobsEndpoint}platform-links/${id}/`, {
            headers: { Authorization: `Token ${token}` },
          }),
        ]);
        console.log(linksResponse.data);
        setJob(jobResponse.data);
        setLinks(linksResponse.data.results || linksResponse.data || []);
      } catch (err) {
        setError("Failed to load job details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchJobDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center min-h-96">
          <p className="text-gray-500">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center min-h-96">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center min-h-96">
          <p className="text-gray-500">Job not found.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-600 border-green-400";
      case "inactive":
        return "bg-red-100 text-red-600 border-red-400";
      case "draft":
        return "bg-yellow-100 text-yellow-600 border-yellow-400";
      default:
        return "bg-gray-100 text-gray-600 border-gray-400";
    }
  };

return (
  <div className="lg:p-8">
    {/* Header */}
    <div className="mb-8 flex justify-between items-center">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 text-gray-600 hover:text-gray-800">
          ← Back
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{job.title}</h1>
          <p className="mt-1 text-sm text-gray-600">Job Details</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/edit_job/${job.id}`)}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Edit Job
        </button>
        <button
          onClick={() => navigate(`/applicants/${job.id}`)}
          className="px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
        >
          View Applicants
        </button>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-6">

        {/* Job Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Overview</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Job Title</h3>
              <p className="text-gray-700">{job.title}</p>
            </div>
            {job.company && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Company</h3>
                <p className="text-gray-700">{job.company}</p>
              </div>
            )}
            {job.logo && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Logo</h3>
                <div className="w-16 h-16 rounded-xl border border-gray-200 overflow-hidden bg-white flex items-center justify-center">
                  <img src={job.logo} alt={`${job.company} logo`} className="w-full h-full object-contain" />
                </div>
              </div>
            )}
            {job.position && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Position</h3>
                <p className="text-gray-700">{job.position}</p>
              </div>
            )}
            {job.type && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Job Type</h3>
                <p className="text-gray-700">{job.type}</p>
              </div>
            )}
            {job.level && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Level</h3>
                <p className="text-gray-700">{job.level}</p>
              </div>
            )}
            {job.time && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Time</h3>
                <p className="text-gray-700">{job.time}</p>
              </div>
            )}
          </div>
        </div>

        {/* Job Description */}
        {job.body && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{job.body}</p>
          </div>
        )}

        {/* Requirements */}
        {job.requirements && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{job.requirements}</p>
          </div>
        )}

        {/* Bonus Incentives */}
        {job.bonuses_incentives && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Bonus Incentives</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{job.bonuses_incentives}</p>
          </div>
        )}

        {/* Other Benefits */}
        {job.other_benefits && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Benefits</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{job.other_benefits}</p>
          </div>
        )}

        {/* AI Evaluation */}
        {job.ai_evaluation && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Evaluation</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{job.ai_evaluation}</p>
          </div>
        )}

        {/* Content Files */}
        {job.content && job.content.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h2>
            <div className="space-y-2">
              {job.content.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 text-sm font-medium">📄</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{file.name || `File ${index + 1}`}</p>
                      <p className="text-sm text-gray-500">{file.type || "Document"}</p>
                    </div>
                  </div>
                  {file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">

        {/* Job Statistics */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Statistics</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Applicants</span>
              <span className="font-semibold text-gray-900">{job.num_applicants || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Avg. AI Score</span>
              <span className="font-semibold text-gray-900">
                {job.avg_ai_score ? job.avg_ai_score.toFixed(1) : "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Status</span>
              <span className={`text-center text-xs border py-1 px-2 rounded-md ${getStatusColor("active")}`}>
                Active
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Posted Date</span>
              <span className="font-semibold text-gray-900">{formatDate(job.date_updated)}</span>
            </div>
          </div>
        </div>

        {/* Platform Links */}
        {links.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Published On</h2>
            <div className="space-y-3">
              {links.map((platformLink) => (
                <a
                  key={platformLink.id}
                  href={platformLink.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full px-4 py-2 text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {platformIcons[platformLink.platform] ?? (
                    <span className="text-xs font-bold text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                      {platformLink.platform?.[0] ?? "?"}
                    </span>
                  )}
                  <span className="font-medium flex-1">{platformLink.platform}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Location & Compensation */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Location & Compensation</h2>
          <div className="space-y-4">
            {job.location && (
              <div>
                <span className="text-gray-600 block mb-1">Location</span>
                <span className="font-semibold text-gray-900">{job.location}</span>
              </div>
            )}
            {(job.city || job.state || job.country) && (
              <div>
                <span className="text-gray-600 block mb-1">Address</span>
                <span className="font-semibold text-gray-900">
                  {[job.city, job.state, job.country].filter(Boolean).join(", ")}
                </span>
              </div>
            )}
            {job.offer_price && (
              <div>
                <span className="text-gray-600 block mb-1">Offer Price</span>
                <span className="font-semibold text-gray-900">${job.offer_price}</span>
              </div>
            )}
            {job.card_type && (
              <div>
                <span className="text-gray-600 block mb-1">Card Type</span>
                <span className="font-semibold text-gray-900">{job.card_type}</span>
              </div>
            )}
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Apply Notifications</span>
              <span className={`text-xs px-2 py-1 rounded-full ${job.quiet_mode ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-600"}`}>
                {job.quiet_mode ? "Enabled" : "Disabled"}
              </span>
            </div>
            {job.url && (
              <div>
                <span className="text-gray-600 block mb-1">Apply URL</span>
                <a
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium break-all"
                >
                  {job.url}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* AI Recommendation */}
        {job.ai_recommendation && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Recommendation</h2>
            <p className="text-gray-700 text-sm">{job.ai_recommendation}</p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/applicants/${job.id}`)}
              className="w-full px-4 py-2 text-left text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              View Applicants
            </button>
            <button
              onClick={() => navigate(`/edit_job/${job.id}`)}
              className="w-full px-4 py-2 text-left text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              Edit Job
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
);
};

export default JobDetails;